import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MenuIcon from "@mui/icons-material/Menu";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLocationContext } from "../../context/LocationContext";

import Logo from "../../assets/images/logoBCT.png";

/* ===== CITY LIST ===== */
const CITIES = [
  { label: "Delhi", path: "/city/delhi" },
  { label: "Mumbai", path: "/city/mumbai" },
  { label: "Varanasi", path: "/city/varanasi" },
  { label: "Lucknow", path: "/city/lucknow" },
  { label: "Kanpur", path: "/city/kanpur" },
];

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  /* ===== LOCATION STATE ===== */
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { city, setCity } = useLocationContext();

  /* ===== Scroll shrink ===== */
  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ===== Helpers ===== */
  const isActive = (path) => location.pathname === path;

  const openWhatsApp = () => {
    window.open(
      "https://wa.me/919532119007?text=Hi%20NearbyGuide%20team",
      "_blank",
    );
  };

  const openLocationMenu = (e) => setAnchorEl(e.currentTarget);
  const closeLocationMenu = () => setAnchorEl(null);

  const changeCity = (selectedCity) => {
    setCity(selectedCity.label); // ✅ global + localStorage
    closeLocationMenu();
  };

  return (
    <>
      {/* ================= HEADER ================= */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: "rgba(13,59,102,0.96)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: scrolled ? 64 : 78,
            transition: "all 0.3s ease",
            px: { xs: 2, md: 3 },
          }}
        >
          {/* LOGO */}
          <Box
            component={Link}
            to="/"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box
              component="img"
              src={Logo}
              alt="NearbyGuide"
              sx={{
                height: scrolled ? 30 : 36,
                transition: "all 0.3s ease",
              }}
            />
          </Box>

          {/* LOCATION SELECTOR */}
          <Box sx={{ ml: 1.5 }}>
            <Button
              onClick={openLocationMenu}
              startIcon={<LocationOnIcon />}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.9rem",
                textTransform: "none",
                px: 1,
              }}
            >
              {city}
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeLocationMenu}
            >
              {CITIES.map((city) => (
                <MenuItem
                  key={city.label}
                  // component={Link}
                  // to={city.path}
                  onClick={() => changeCity(city)}
                >
                  {city.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* ================= DESKTOP NAV ================= */}
          {!isMobile && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                gap: { md: 1.6, lg: 2 }, // 👈 tighter spacing
              }}
            >
              {[
                { label: "Home", path: "/" },
                { label: "About", path: "/about" },
                { label: "Cities", path: "/city" },
                ...CITIES,
              ].map((item) => {
                const isCity =
                  item.label === "Kanpur" ||
                  item.label === "Lucknow" ||
                  item.label === "Varanasi";

                return (
                  <Box key={item.path} sx={{ position: "relative" }}>
                    <Button
                      component={Link}
                      to={item.path}
                      sx={{
                        color: "#fff",
                        fontWeight: isCity ? 500 : 600,
                        fontSize: "0.9rem", // 👈 smaller text
                        letterSpacing: "0.2px",
                        opacity: isActive(item.path) ? 1 : 0.8,
                        px: 1.1, // 👈 less padding
                        minWidth: "auto",
                      }}
                    >
                      {item.label}
                    </Button>

                    {isActive(item.path) && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -6,
                          left: "30%",
                          width: "40%",
                          height: 2, // 👈 thinner underline
                          borderRadius: 2,
                          backgroundColor: "#F4D35E",
                          opacity: 0.9,
                        }}
                      />
                    )}
                  </Box>
                );
              })}

              {/* CHAT CTA */}
              <Button
                onClick={openWhatsApp}
                startIcon={<WhatsAppIcon />}
                sx={{
                  backgroundColor: "#F4D35E",
                  color: "#0D3B66",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  px: 2.4,
                  py: 0.9,
                  borderRadius: 999,
                  boxShadow: "0 10px 24px rgba(244,211,94,0.35)",
                  "&:hover": {
                    backgroundColor: "#E6BF3C",
                  },
                }}
              >
                Chat
              </Button>
            </Stack>
          )}

          {/* ================= MOBILE MENU ICON ================= */}
          {isMobile && (
            <IconButton onClick={() => setOpen(true)}>
              <MenuIcon sx={{ color: "#fff" }} />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* ================= MOBILE DRAWER ================= */}
      <Drawer
        anchor="top"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#0D3B66",
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
            pb: 4,
          },
        }}
      >
        <Stack spacing={3} sx={{ px: 3, pt: 3 }}>
          <Box component="img" src={Logo} sx={{ width: 160 }} />

          <Button
            component={Link}
            to="/"
            onClick={() => setOpen(false)}
            sx={{ color: "#fff" }}
          >
            Home
          </Button>
          <Button
            component={Link}
            to="/about"
            onClick={() => setOpen(false)}
            sx={{ color: "#fff" }}
          >
            About
          </Button>
          <Button
            component={Link}
            to="/city"
            onClick={() => setOpen(false)}
            sx={{ color: "#fff" }}
          >
            Cities
          </Button>

          <Button
            onClick={() => {
              setOpen(false);
              openWhatsApp();
            }}
            startIcon={<WhatsAppIcon />}
            sx={{
              backgroundColor: "#F4D35E",
              color: "#0D3B66",
              fontWeight: 700,
              py: 1.3,
              borderRadius: 999,
            }}
          >
            WhatsApp Chat
          </Button>
        </Stack>
      </Drawer>
    </>
  );
};

export default Header;
